/* global window */

import videojs from 'video.js';
import get from 'lodash/get';
import URL from 'url-parse';

/**
 * Wrapper to handle uncaught Promise errors.
 * @param {Promise} promise Promise without error handling.
 * @return {Promise} Promise with error handling.
 */
export function catchErrors(promise) {
  return promise
    .then(data => data)
    .catch(error => {
      videojs.log.error(error);
      return null;
    });
}

/**
 * Returns a promise that resolves when DOM is loaded.
 *
 * @param {Object} document Window document.
 * @return {Promise<boolean>} Resolves to true when DOM loaded.
 */
export function whenDomReady(document) {
  return new Promise(resolve => {
    document = document || window.document;

    const done = () => resolve(true);

    if (['interactive', 'complete'].includes(document.readyState)) {
      return done();
    }

    document.addEventListener('DOMContentLoaded', done);
  });
}

/**
 * Returns a promise that resolves when script is loaded.
 *
 * @param {String} scriptSrc Script URL.
 * @return {Promise<boolean>} Resolves to true when script loaded.
 */
export function loadScript(scriptSrc) {
  const head = document.getElementsByTagName('head')[0] || document.documentElement;

  const script = document.createElement('script');
  script.charset = 'utf-8';
  script.type = 'text/javascript';
  script.src = scriptSrc;

  return new Promise(function(resolve, reject) {
    let done = false;

    script.onload = script.onreadystatechange = () => {
      if (!done && (!script.readyState || ['loaded', 'complete'].includes(script.readyState))) {
        done = true;
        script.onload = script.onreadystatechange = null;
        return resolve(true);
      }
    };

    script.onerror = () => reject(false);

    head.appendChild(script);
  });
}

/**
 * Check if browser is Safari.
 * @param {Object} navigator A Navigator instance.
 * @returns {boolean}
 */
export function isBrowserSafari(navigator) {
  return (
    navigator.vendor &&
    navigator.vendor.indexOf('Apple') > -1 &&
    navigator.userAgent &&
    !navigator.userAgent.match('CriOS')
  );
}

/**
 * Returns the resized URL of a poster image.
 * @param {string} originalUrl - original poster image URL.
 * @param {integer} size - new image size.
 * @param {integer} quality - new image quality.
 * @return {string}
 */
export function resizePoster(originalUrl, size = 810, quality = 70) {
  const parsedUrl = new URL(originalUrl, true);

  if (!get(parsedUrl.query, 'resize')) {
    parsedUrl.query.resize = `${size}:*`;
  }

  if (!get(parsedUrl.query, 'quality')) {
    parsedUrl.query.quality = `${quality}`;
  }

  const isPNG = parsedUrl.pathname.endsWith('.png') || parsedUrl.pathname.endsWith('.PNG');
  if (isPNG && parsedUrl.query.quality) {
    delete parsedUrl.query.quality;
  }

  return parsedUrl.toString();
}

/**
 * Generate a random ID string.
 */
export function generateRandomId() {
  return Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
}

/**
 * Check if browser is Edge.
 * @param {Object} navigator - A Navigator instance.
 * @returns {bool}
 */
export function isBrowserEdge(navigator) {
  const ua = navigator.userAgent;

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:');
    return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  return false;
}

/**
 * jquery $(document).width() does not take in consideration the scrollbar
 * and gives a different value than the one matched in the media query
 * and this will work faster than matchMedia() api for now
 */
export function calculateViewport() {
  let elem;
  let prefix;
  const hasInnerWidthProperty = Object.prototype.hasOwnProperty.call(window, 'innerWidth');

  if (!hasInnerWidthProperty) {
    prefix = 'client';
    elem = document.documentElement || document.body;
  } else {
    elem = window;
    prefix = 'inner';
  }

  return { width: elem[`${prefix}Width`], height: elem[`${prefix}Height`] };
}

// check_webp_feature:
//   'feature' can be one of 'lossy', 'lossless', 'alpha' or 'animation'.
//   'callback(feature, result)' will be passed back the detection result (in an asynchronous way!)
//    source: https://developers.google.com/speed/webp/faq#how_can_i_detect_browser_support_for_webp
export function checkWebPFeature(feature, callback) {
  const kTestImages = {
    lossy: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
    lossless: 'UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==',
    alpha: 'UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==',
    animation: 'UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA',
  };
  const img = new Image();
  img.onload = () => {
    const result = (img.width > 0) && (img.height > 0);
    callback(feature, result);
  };
  img.onerror = () => {
    callback(feature, false);
  };
  img.src = `data:image/webp;base64,${kTestImages[feature]}`;
}

/**
 * Preload an image.
 * @param {string} url Image URL.
 */
export function preloadImage(url) {
  const img = new Image();
  img.src = url;
}
